import { debug, warn } from '@4players/odin-common';

import type { SharedFrameBufferMeta } from '../raw-frame';
import type { FromWorker, ToWorker } from './protocol';

/** Main-thread handle to the single render worker shared by every
 *  `NativeRawVideoReceiver`. The worker owns all transferred
 *  `OffscreenCanvas` backings and does the texture uploads + draws;
 *  this client only allocates ids, forwards buffers (always as
 *  transfers — ownership moves, bytes don't copy) and relays the
 *  worker's stats/warnings into the bridged logger.
 *
 *  Whether worker rendering is used at all is decided ONCE per app
 *  run by `renderMode()`: a canvas whose backing was transferred can
 *  never be painted from the main thread again, so the choice must be
 *  made before the first paint and cannot flip afterwards. When the
 *  probe fails (old WebKitGTK / WKWebView without OffscreenCanvas
 *  WebGL or worker rAF) everything stays on the proven main-thread
 *  path — same capability-fallback philosophy as the WebView2 shared
 *  buffers. */
export class RenderWorkerClient {
  private worker: Worker;
  private nextSid = 1;
  private nextCid = 1;
  /** DOM canvas → worker canvas id. Weak so a canvas dropped by
   *  Angular without an unmount doesn't pin its entry (the worker-
   *  side OffscreenCanvas is reclaimed via `stream-close`). */
  private canvasIds = new WeakMap<HTMLCanvasElement, number>();
  /** Tells the worker when a transferred canvas element has actually been
   *  collected. Without it the worker has no safe moment to drop its
   *  `OffscreenCanvas`: a stream closing says nothing about the element's
   *  lifetime, and dropping too early is unrecoverable — the backing was
   *  transferred, so neither side can ever paint that element again. */
  private readonly collected = new FinalizationRegistry<number>((cid) => {
    if (this.disposed) return;
    this.post({ t: 'canvas-gone', cid });
  });
  private disposed = false;
  private probeResolve: ((ok: boolean) => void) | null = null;
  private readonly visListener = () => {
    this.post({ t: 'page-visible', visible: !document.hidden });
  };

  constructor() {
    this.worker = new Worker(new URL('./render.worker', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (e: MessageEvent<FromWorker>) => this.onMessage(e.data);
    // Worker rAF ignores page visibility (unlike main-thread rAF,
    // which auto-throttles) — forward it so a minimised window
    // doesn't keep painting invisible frames.
    document.addEventListener('visibilitychange', this.visListener);
    this.post({ t: 'page-visible', visible: !document.hidden });
  }

  probe(timeoutMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        this.probeResolve = null;
        resolve(false);
      }, timeoutMs);
      this.probeResolve = (ok) => {
        clearTimeout(timer);
        resolve(ok);
      };
      this.post({ t: 'probe' });
    });
  }

  dispose(): void {
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.visListener);
    this.worker.terminate();
  }

  allocStream(): number {
    const sid = this.nextSid++;
    this.post({ t: 'stream-new', sid });
    return sid;
  }

  closeStream(sid: number): void {
    this.post({ t: 'stream-close', sid });
  }

  /** Transfer (or re-activate) a canvas into the worker. Returns the
   *  canvas id for unmount/visibility calls, or `null` when the
   *  transfer failed — the caller logs and skips that canvas. */
  mountCanvas(sid: number, canvas: HTMLCanvasElement): number | null {
    const existing = this.canvasIds.get(canvas);
    if (existing !== undefined) {
      this.post({ t: 'remount', sid, cid: existing });
      return existing;
    }
    let off: OffscreenCanvas;
    try {
      off = canvas.transferControlToOffscreen();
    } catch (e) {
      warn(`[raw-preview] transferControlToOffscreen failed: ${e}`);
      return null;
    }
    const cid = this.nextCid++;
    this.canvasIds.set(canvas, cid);
    this.collected.register(canvas, cid);
    this.post({ t: 'mount', sid, cid, canvas: off }, [off]);
    return cid;
  }

  unmountCanvas(cid: number): void {
    this.post({ t: 'unmount', cid });
  }

  attachSharedBuffer(sid: number, buffer: ArrayBuffer, meta: SharedFrameBufferMeta): void {
    this.post({ t: 'shm', sid, buffer, meta }, [buffer]);
  }

  detachSharedBuffer(sid: number): void {
    this.post({ t: 'shm-detach', sid });
  }

  pushFrame(sid: number, buffer: ArrayBuffer): void {
    this.post({ t: 'frame', sid, buffer }, [buffer]);
  }

  setCanvasVisible(cid: number, visible: boolean): void {
    this.post({ t: 'canvas-visible', cid, visible });
  }

  private post(msg: ToWorker, transfer?: Transferable[]): void {
    if (transfer) {
      this.worker.postMessage(msg, transfer);
    } else {
      this.worker.postMessage(msg);
    }
  }

  private onMessage(msg: FromWorker): void {
    switch (msg.t) {
      case 'probe-result':
        this.probeResolve?.(msg.ok);
        this.probeResolve = null;
        if (!msg.ok) {
          warn(`[raw-preview] render worker unavailable: ${msg.detail}`);
        }
        break;
      case 'stats':
        // Same shape as the main-thread `[raw-preview] perf` line so
        // both pipelines grep alike; sid disambiguates streams.
        debug(`[raw-preview] worker perf sid=${msg.sid} ${msg.line}`);
        break;
      case 'gl-failed':
        warn(
          `[raw-preview] worker WebGL init failed for canvas cid=${msg.cid} (sid=${msg.sid}) — canvas stays blank`,
        );
        break;
    }
  }
}

let client: RenderWorkerClient | null = null;
let modePromise: Promise<'worker' | 'main'> | null = null;

/** Decide (once per app run) whether painting happens in the render
 *  worker or on the main thread. Resolves fast: the probe is one
 *  worker round-trip. */
export function renderMode(): Promise<'worker' | 'main'> {
  if (modePromise) return modePromise;
  modePromise = (async () => {
    try {
      if (
        typeof Worker === 'undefined' ||
        typeof OffscreenCanvas === 'undefined' ||
        typeof HTMLCanvasElement === 'undefined' ||
        !('transferControlToOffscreen' in HTMLCanvasElement.prototype)
      ) {
        return 'main';
      }
      const candidate = new RenderWorkerClient();
      const ok = await candidate.probe(3000);
      if (!ok) {
        candidate.dispose();
        return 'main';
      }
      client = candidate;
      debug('[raw-preview] render worker active — painting off the main thread');
      return 'worker';
    } catch (e) {
      warn(`[raw-preview] render worker setup failed, painting on main thread: ${e}`);
      return 'main';
    }
  })();
  return modePromise;
}

/** The active client — non-null exactly when `renderMode()` resolved
 *  to `'worker'`. */
export function renderWorker(): RenderWorkerClient | null {
  return client;
}
